import { useMemo, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import {
  Badge, Box, Button, ButtonGroup, Checkbox, Drawer, DrawerBody, DrawerCloseButton,
  DrawerContent, DrawerFooter, DrawerHeader, DrawerOverlay, Flex, Heading, HStack,
  IconButton, Input, Link, Select, SimpleGrid, Table, Tbody, Td, Text, Textarea,
  Th, Thead, Tooltip, Tr, VStack, Tag,
} from '@chakra-ui/react';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import ConfirmDialog from '../../components/ConfirmDialog';
import TruncatedCell from '../../components/TruncatedCell';
import type { CurrencyCode, Judge, Maker, StatusFilter, StatusRow } from '../../contexts/types';
import GoogleAPI from '../../utils/GoogleAPI';
import { sanitizeFileName } from '../../utils/utils';

/**
 * ヒアリング用サンプルデータ（UIモックと同一の6件）。
 * 実装では A列は投資申請テーブルから、B/C列は状況報告テーブルから取得する。
 */
const initialRows: StatusRow[] = [
  {
    id: 'r1',
    hq: 'モーター・ライティング&センシング事業本部', dept: 'モーター部門', unit: 'PMモーター事業部',
    planNo: '25A-MA601', applyPeriod: '2025/7', applyAmountM: 29,
    name: 'ブラスター自動機 SG-8B-310', coCode: '7K', companies: ['Minebea(Cambodia)'],
    applyNo: '6-MA25029', orderDate: '2025-07-15', orderCompanies: ['関東物産㈱'],
    makers: [{ name: '㈱不二製作所', currency: 'YN', amount: '28,900', code: '02', company: 'ミネベアミツミ㈱' }],
    orderCurrency: 'YN', orderAmount: '28,900', orderSrcCode: '02', orderSrcCompany: 'ミネベアミツミ㈱',
    inspectDate: '2026-01-20', inspectCurrencies: ['YN'], inspectAmountOrig: '28,900', inspectAmountJpy: '28,900',
    judge: 'ok', payPeriod: '2026-02', paySrcCode: '02', paySrcCompany: 'ミネベアミツミ㈱',
    remarks: '', done: true, dirty: false,
  },
  {
    id: 'r2',
    hq: 'ユーシン事業本部', dept: 'ユーシン事業部門', unit: '自動車事業部',
    planNo: 'A05-24122-A', applyPeriod: '2024/8', applyAmountM: 29,
    name: 'J69P_サイドドアラッチ_現行部品流用による量産増面金型製作', coCode: 'UT', companies: ['有信制造(中山)'],
    applyNo: 'A05-24122-A', orderDate: '2024-09-01',
    orderCompanies: ['京都電工（東莞）有限公司', '东莞市海特精密塑胶制品有限公司'],
    makers: [
      { name: '京都電工（東莞）有限公司', currency: 'CNY', amount: '1,354', code: 'UT', company: '有信制造(中山)有限公司' },
      { name: '东莞市海特精密塑胶制品', currency: 'CNY', amount: '', code: 'UT', company: '有信制造(中山)有限公司' },
    ],
    orderCurrency: 'CNY', orderAmount: '1,354', orderSrcCode: 'UT', orderSrcCompany: '有信制造(中山)有限公司',
    inspectDate: '2026-01-01', inspectCurrencies: ['CNY'], inspectAmountOrig: '1,354', inspectAmountJpy: '28,320',
    judge: 'ok', payPeriod: '2026-03', paySrcCode: 'UT', paySrcCompany: '有信制造（中山）有限公司',
    remarks: '金型残課題未解決による', done: true, dirty: false,
  },
  {
    id: 'r3',
    hq: 'セミコンダクタ&エレクトロニクス事業本部', dept: 'EMS部門', unit: 'SC事業部',
    planNo: 'M0-2504-701', applyPeriod: '2025/4', applyAmountM: 153,
    name: '超音波センサ用コイル TBE-736273MD（青島3ライン目増設）', coCode: 'MJ', companies: ['青島三美'],
    applyNo: 'M0-2504-701', orderDate: '2025-07-08', orderCompanies: ['青島三美'],
    makers: [{ name: '青島三美', currency: 'USD', amount: '976', code: 'MJ', company: '青島三美' }],
    orderCurrency: 'USD', orderAmount: '976', orderSrcCode: 'MJ', orderSrcCompany: '青島三美',
    inspectDate: '2026-02-25', inspectCurrencies: ['USD'], inspectAmountOrig: '976', inspectAmountJpy: '149,900',
    judge: 'ok', payPeriod: '2026-03', paySrcCode: 'MJ', paySrcCompany: '青島三美',
    remarks: '', done: true, dirty: false,
  },
  {
    id: 'r4',
    hq: 'モーター・ライティング&センシング事業本部', dept: 'モーター部門', unit: 'モアテック事業部',
    planNo: '25-MKM-7', applyPeriod: '2025/11', applyAmountM: 100,
    name: 'Hanon System向け3.5G AGA用の量産ライン', coCode: '7U,7L', companies: ['MPI', 'Moatech'],
    applyNo: '25-MKM-7', orderDate: '2025-11-01',
    orderCompanies: ['M-Pro', 'D&Sync', 'EV Laser', 'Halim ENG、Beidi'],
    makers: [
      { name: 'M-Pro', currency: 'KRW', amount: '924,380', code: '', company: 'MPI,Moatech' },
      { name: 'D&Sync', currency: 'USD', amount: '8', code: '', company: 'MPI,Moatech' },
      { name: 'EV Laser', currency: 'KRW', amount: '12,500', code: '', company: 'MPI,Moatech' },
      { name: 'Halim ENG、Beidi', currency: 'USD', amount: '3.2', code: '', company: 'MPI,Moatech' },
    ],
    orderCurrency: 'KRW', orderAmount: '924,380', orderSrcCode: '', orderSrcCompany: 'MPI,Moatech',
    inspectDate: '2026-03-01', inspectCurrencies: ['KRW', 'USD'], inspectAmountOrig: '', inspectAmountJpy: '143,200',
    judge: 'over', payPeriod: '2026-05', paySrcCode: '', paySrcCompany: 'MPI,Moatech',
    remarks: '資材費上昇のため追加申請予定', done: false, dirty: false,
  },
  {
    id: 'r5',
    hq: 'セミコンダクタ&エレクトロニクス事業本部', dept: '半導体部門', unit: '半導体事業部',
    planNo: 'FY25-168', applyPeriod: '2025/11', applyAmountM: 32,
    name: 'DFN老朽化更新', coCode: 'MD', companies: ['セブミツミ'],
    applyNo: 'FY25-168', orderDate: '2025-11-12', orderCompanies: ['㈱新川', 'ASMPT JAPAN㈱'],
    makers: [
      { name: '㈱新川', currency: 'JPY', amount: '32,694', code: 'MO', company: 'ミツミ電機（株）' },
      { name: 'ASMPT JAPAN㈱', currency: 'USD', amount: '', code: 'MO', company: 'ミツミ電機（株）' },
    ],
    orderCurrency: 'JPY', orderAmount: '32,694', orderSrcCode: 'MO', orderSrcCompany: 'ミツミ電機（株）',
    inspectDate: '2026-03-25', inspectCurrencies: ['JPY', 'USD'], inspectAmountOrig: '', inspectAmountJpy: '32,694',
    judge: 'ok', payPeriod: '2026-06', paySrcCode: 'MO', paySrcCompany: 'ミツミ電機（株）',
    remarks: '', done: false, dirty: false,
  },
  {
    id: 'r6',
    hq: 'モーター・ライティング&センシング事業本部', dept: 'モーター部門', unit: 'BLモーター事業部',
    planNo: 'ZNB251001', applyPeriod: '2025/10', applyAmountM: 48,
    name: 'BLIM 新構造モータライン ハウジングAssy工程自動化', coCode: '9J', companies: ['ZHMM'],
    applyNo: 'ZNB251001', orderDate: '2025-10-01',
    orderCompanies: ['珠海林顺机电有限公司', '珠海攀丰自動化設備有限公司'],
    makers: [
      { name: '珠海林顺机电有限公司', currency: 'RMB', amount: '2,328', code: '9J', company: 'ZHMM' },
      { name: '珠海攀丰自動化設備有限公司', currency: 'RMB', amount: '', code: '9J', company: 'ZHMM' },
    ],
    orderCurrency: 'RMB', orderAmount: '2,328', orderSrcCode: '9J', orderSrcCompany: 'ZHMM',
    inspectDate: '2026-04-01', inspectCurrencies: ['RMB'], inspectAmountOrig: '2,328', inspectAmountJpy: '49,600',
    judge: 'ok', payPeriod: '2026-07', paySrcCode: '9J', paySrcCompany: 'ZHMM',
    remarks: '2026年4月计上：B1054835/B1054836', done: false, dirty: false,
  },
];

/** GoogleSpreadで入力: 出力先ドライブ内フォルダパス */
const SPREAD_SHEET_PATH = 'CEAS/SpreadSheet/StatusReport';

/** GoogleSpreadで入力: 書き出す列見出し（COLS の k に対応。'ops' は対象外） */
const SPREAD_HEADER_LABELS: Record<string, string> = {
  planNo: '案件No.', name: '設備件名', hq: '製造本部', dept: '部門', unit: '事業部',
  applyPeriod: '申請時期', applyAmountM: '申請金額(百万円)', coCode: '会社コード',
  companies: '会社名', orderCompanies: '発注先会社', makers: 'メーカー内訳',
  orderAmount: '発注金額(千)', inspectDate: '検収時期', inspectAmountJpy: '検収金額(円換算・千)',
  judge: '判定', remarks: '備考', done: '完了',
};
/**
 * 仮通貨
 */
const CURRENCIES: CurrencyCode[] = ['YN', 'JPY', '円', 'USD', 'CNY', 'KRW', 'RMB', 'PHP'];

// 申請額判定（Judge）ごとの表示メタ情報。バッジの文言と colorScheme をまとめて管理する。
const JUDGE_META: Record<Judge, { text: string; scheme: string }> = {
  ok: { text: '申請額内', scheme: 'green' },
  over: { text: '超過', scheme: 'red' },
  none: { text: '未入力', scheme: 'gray' },
};

// 一覧のソート状態。key は列キー（COLS の k と対応）、ascending は昇順かどうか。
type SortConfig = { key: string; ascending: boolean };

/** 列定義。ref=true は「経営管理部 参照」折りたたみ時に非表示。grp で見出し色分け。 */
type ColDef = { k: string; w: number; grp: 'A' | 'B' | 'C' | 'X'; ref?: boolean };
// テーブルの列一覧（表示順そのまま）。k: StatusRow のフィールド名（'ops' は操作列のダミーキー）、
// w: 列幅(px、colgroup に反映)、grp: 見出しグループ（A=申請参照/B=事業部報告/C=自動算出/X=操作）、
// ref: true の列は「経営管理部 参照」トグルが閉じている間は非表示にする。
const COLS: ColDef[] = [
  { k: 'planNo', w: 140, grp: 'A' },
  { k: 'name', w: 260, grp: 'A' },
  { k: 'hq', w: 170, grp: 'A', ref: true },
  { k: 'dept', w: 130, grp: 'A', ref: true },
  { k: 'unit', w: 140, grp: 'A', ref: true },
  { k: 'applyPeriod', w: 108, grp: 'A', ref: true },
  { k: 'applyAmountM', w: 116, grp: 'A', ref: true },
  { k: 'coCode', w: 90, grp: 'A', ref: true },
  { k: 'companies', w: 210, grp: 'A', ref: true },
  { k: 'orderCompanies', w: 170, grp: 'B' },
  { k: 'makers', w: 180, grp: 'B' },
  { k: 'orderAmount', w: 118, grp: 'B' },
  { k: 'inspectDate', w: 108, grp: 'B' },
  { k: 'inspectAmountJpy', w: 160, grp: 'C' },
  { k: 'judge', w: 110, grp: 'C' },
  { k: 'remarks', w: 66, grp: 'B' },
  { k: 'done', w: 90, grp: 'B' },
  { k: 'ops', w: 78, grp: 'X' },
];

// グループごとの見出しセル配色（背景/文字色）。A=申請参照(黄)、B=事業部報告(緑・淡)、C=自動算出(緑・濃)、X=操作(グレー)。
const GRP_TH_BG: Record<ColDef['grp'], string> = { A: 'yellow.50', B: 'green.50', C: 'green.100', X: 'gray.50' };
const GRP_TH_FG: Record<ColDef['grp'], string> = { A: 'yellow.800', B: 'green.800', C: 'green.800', X: 'gray.600' };

/** 「先頭 ＋N」形式の要約チップ。全件はツールチップで表示。 */
function SummaryChip({ items, emptyLabel = '（未入力）' }: { items: string[]; emptyLabel?: string }) {
  const list = items.filter((x) => x && x !== '-');
  if (list.length === 0) return <Text color="gray.400" isTruncated>{emptyLabel}</Text>;
  return (
    <Tooltip label={list.join('、')} hasArrow openDelay={400} placement="top">
      <HStack spacing={1} maxW="100%">
        <Text isTruncated>{list[0]}</Text>
        {list.length > 1 && (
          <Tag size="sm" colorScheme="blue" borderRadius="full" flexShrink={0}>＋{list.length - 1}</Tag>
        )}
      </HStack>
    </Tooltip>
  );
}

export default function StatusReport() {
  /**一覧データ本体（サンプルデータで初期化。将来はAPI等から取得予定）*/
  const [rows, setRows] = useState<StatusRow[]>(initialRows);

  /**編集ドロワーを開いている行のid（nullなら閉じている） */
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  /**行削除の確認ダイアログの表示状態 */
  const [confirmRowDelete, setConfirmRowDelete] = useState(false);

  /**「経営管理部 参照」列(grp=A かつ ref=true)を畳んでいるか */
  const [leftCollapsed, setLeftCollapsed] = useState(true);

  /**上部タブでの絞込（全件／未検収／超過） */
  const [filter, setFilter] = useState<StatusFilter>('all');

  /** 一覧のソート状態 */
  const [sort, setSort] = useState<SortConfig>({ key: 'planNo', ascending: true });

  /**列ヘッダの絞込入力値（列キー→検索文字列 or 選択値） */
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const setColFilter = (key: string, v: string) => setColFilters((f) => ({ ...f, [key]: v }));
  const clearColFilters = () => setColFilters({}); // 全列フィルタを一括クリア

  /** GoogleSpreadで入力: 書き出し中フラグ・開いたスプレッドシートのウィンドウ参照 */
  const [isSpreadLoading, setIsSpreadLoading] = useState(false);
  const [spreadSheetWindow, setSpreadSheetWindow] = useState<Window | null>(null);
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(null);
  /** 入力モード：画面上で直接編集 / GoogleSpreadで編集（Equipment.tsx と同じ切替パターン） */
  const [inputMode, setInputMode] = useState<'manual' | 'google'>('manual');
  const googleAPI = useMemo(() => new GoogleAPI(), []);

  /** 行1件を GoogleSpread の1セル文字列に変換（列キーごとに整形） */
  const toSpreadValue = (row: StatusRow, key: string): string => {
    switch (key) {
      case 'companies': return row.companies.join('、');
      case 'orderCompanies': return row.orderCompanies.join('、');
      case 'makers': return row.makers.map((m) => `${m.company}/${m.name}:${m.currency}${m.amount}`).join('、');
      case 'applyAmountM': return row.applyAmountM != null ? String(row.applyAmountM) : '';
      case 'judge': return JUDGE_META[row.judge].text;
      case 'done': return row.done ? '済' : '未';
      default: return String((row as unknown as Record<string, unknown>)[key] ?? '');
    }
  };

  /** GoogleSpreadで入力: 現在の一覧データを新規スプレッドシートに書き出して開く */
  const handleGoogleSpreadClick = async () => {
    setInputMode('google');
    setIsSpreadLoading(true);
    try {
      if (!(await googleAPI.initGapi())) throw new Error('Google API の初期化に失敗しました');
      if (!(await googleAPI.signIn())) throw new Error('Google サインインに失敗しました');

      const fileName = sanitizeFileName(`StatusReport_${new Date().toISOString().slice(0, 10)}`);
      const parentId = await googleAPI.createFolderPath(SPREAD_SHEET_PATH, null);
      const newSpreadsheetId = await googleAPI.createSpreadSheet(fileName, parentId!, null);
      if (!newSpreadsheetId) throw new Error('スプレッドシートの作成に失敗しました');
      setActiveSpreadsheetId(newSpreadsheetId);

      const newWindow = window.open(googleAPI.getOpenSpradSheetUrl(newSpreadsheetId), '_blank');
      setSpreadSheetWindow(newWindow);

      const exportCols = COLS.filter((c) => c.k !== 'ops').map((c) => c.k);
      const HEADER = exportCols.map((k) => SPREAD_HEADER_LABELS[k] ?? k);
      const values = rows.map((r) => exportCols.map((k) => toSpreadValue(r, k)));

      const sheetName = (await googleAPI.getFirstSheetName(newSpreadsheetId)) ?? GoogleAPI.DEFAULT_SHEET_NAME;
      await googleAPI.spreadSheetSetValue(newSpreadsheetId, sheetName, 1, 1, [HEADER, ...values]);

      if (newWindow) {
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            setSpreadSheetWindow(null);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Change GoogleSpread Error.', error);
    } finally {
      setIsSpreadLoading(false);
    }
  };

  /** 画面上で入力: GoogleSpread編集を終え、画面上の直接編集モードへ戻す（Equipment.tsx の handleMaualClick 相当） */
  const handleManualClick = () => {
    spreadSheetWindow?.close();
    setSpreadSheetWindow(null);
    setInputMode('manual');
  };

  /** スプレッドシート1セルの文字列を StatusRow の該当フィールドへ書き戻す（toSpreadValue の逆変換） */
  const fromSpreadValue = (row: StatusRow, key: string, value: string): Partial<StatusRow> => {
    switch (key) {
      case 'companies': return { companies: value.split('、').filter(Boolean) };
      case 'orderCompanies': return { orderCompanies: value.split('、').filter(Boolean) };
      case 'applyAmountM': return { applyAmountM: value === '' ? null : Number(value) };
      case 'done': return { done: value === '済' };
      // メーカー内訳・判定は構成が複雑なため同期対象外（画面上で入力してください）
      case 'makers':
      case 'judge':
        return {};
      default: return { [key]: value } as Partial<StatusRow>;
    }
  };

  /** データ連携: GoogleSpread側の最新値を読み込み、一覧データへ反映する（Equipment.tsx の handleSyncData 相当） */
  const handleSyncData = async () => {
    if (!activeSpreadsheetId) return;
    setIsSpreadLoading(true);
    try {
      if (!(await googleAPI.initGapi())) throw new Error('Google API の初期化に失敗しました');
      if (!(await googleAPI.signIn())) throw new Error('Google サインインに失敗しました');

      const sheetName = (await googleAPI.getFirstSheetName(activeSpreadsheetId)) ?? GoogleAPI.DEFAULT_SHEET_NAME;
      const exportCols = COLS.filter((c) => c.k !== 'ops').map((c) => c.k);
      const { values } = await googleAPI.spreadSheetGetValues(activeSpreadsheetId, sheetName);
      const dataRows = (values ?? []).slice(1); // 先頭行はヘッダーなので除外

      setRows((rs) => rs.map((r, i) => {
        const line = dataRows[i];
        if (!line) return r;
        const patch = exportCols.reduce<Partial<StatusRow>>((acc, k, ci) => {
          const cell = line[ci];
          return cell === undefined ? acc : { ...acc, ...fromSpreadValue(r, k, String(cell)) };
        }, {});
        return { ...r, ...patch, dirty: true };
      }));
    } catch (error) {
      console.error('Change GoogleSpread Sync Error.', error);
    } finally {
      setIsSpreadLoading(false);
    }
  };

  // ── 更新ヘルパ（すべて immutable・更新した行に dirty=true を立てる） ──
  /**updateRow: 指定idの行だけを fn で差し替え、他の行はそのまま。更新後は dirty(未保存) フラグを立てる共通処理。*/
  const updateRow = (id: string, fn: (r: StatusRow) => StatusRow) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...fn(r), dirty: true } : r)));

  /**setField: StatusRow の任意の1フィールドを書き換える汎用セッター（型はキーに応じて自動推論）*/
  const setField = <K extends keyof StatusRow>(id: string, key: K, value: StatusRow[K]) =>
    updateRow(id, (r) => ({ ...r, [key]: value }));

  /**発注先会社名は配列（複数社対応）。i番目の値だけ書き換える。*/
  const setOrderCompany = (id: string, i: number, v: string) =>
    updateRow(id, (r) => ({ ...r, orderCompanies: r.orderCompanies.map((x, j) => (j === i ? v : x)) }));

  /** 発注先会社名の入力欄を1行追加（末尾に空文字を追加）*/
  const addOrderCompany = (id: string) =>
    updateRow(id, (r) => ({ ...r, orderCompanies: [...r.orderCompanies, ''] }));

  /** 発注先会社名のi番目を削除*/
  const removeOrderCompany = (id: string, i: number) =>
    updateRow(id, (r) => ({ ...r, orderCompanies: r.orderCompanies.filter((_, j) => j !== i) }));

  /** メーカー内訳（1行=1メーカー・通貨・金額）のi番目の任意フィールドを書き換える*/
  const setMakerField = <K extends keyof Maker>(id: string, i: number, key: K, v: Maker[K]) =>
    updateRow(id, (r) => ({ ...r, makers: r.makers.map((m, j) => (j === i ? { ...m, [key]: v } : m)) }));

  /** メーカー内訳を1行追加（初期値は空欄・通貨は仮通貨'YN'）*/
  const addMaker = (id: string) =>
    updateRow(id, (r) => ({ ...r, makers: [...r.makers, { name: '', currency: 'YN', amount: '', code: '', company: '' }] }));

  /** メーカー内訳のi番目を削除*/
  const removeMaker = (id: string, i: number) =>
    updateRow(id, (r) => ({ ...r, makers: r.makers.filter((_, j) => j !== i) }));

  /** 検収完了チェックボックスのオン/オフを反転*/
  const toggleDone = (id: string) => updateRow(id, (r) => ({ ...r, done: !r.done }));

  /** 行削除。開いているドロワーが削除対象と同じ行なら閉じる。*/
  const removeRow = (id: string) => {
    setRows((rs) => rs.filter((r) => r.id !== id));
    setOpenRowId((cur) => (cur === id ? null : cur));
  };

  // 一括保存：全行の dirty フラグを一括で false に落とす（実際の永続化はまだ未実装／サンプル用）
  const saveAll = () => setRows((rs) => rs.map((r) => ({ ...r, dirty: false })));
  const saveDrawer = () => {
    // 超過時は理由入力必須（保存ボタン側でも disabled 制御しているが、防御的に二重チェック）
    if (overReasonError) return;
    setRows((rs) => rs.map((r) => (r.id === openRowId ? { ...r, dirty: false } : r)));
    setOpenRowId(null);
  };

  // 新規行を追加してそのままドロワーを開く。
  // 「経営管理部 参照」項目（hq/dept/unit/planNo/applyPeriod/applyAmountM/coCode/companies）は
  // 本来CEAS側から連携される想定のため、申請額5百万円以下＝申請不要な新規案件を表す仮の値('-'等)で埋める。
  const addNewRow = () => {
    const id = `new${Date.now()}`;
    const blank: StatusRow = {
      id, hq: '-', dept: '-', unit: '-', planNo: '（新規）', applyPeriod: '-', applyAmountM: null,
      name: '（申請金額5百万円以下の新規行）', coCode: '-', companies: ['-'],
      applyNo: '', orderDate: '', orderCompanies: [''],
      makers: [{ name: '', currency: 'YN', amount: '', code: '', company: '' }],
      orderCurrency: 'YN', orderAmount: '', orderSrcCode: '', orderSrcCompany: '',
      inspectDate: '', inspectCurrencies: ['YN'], inspectAmountOrig: '', payPeriod: '',
      paySrcCode: '', paySrcCompany: '', remarks: '', done: false,
      inspectAmountJpy: '', judge: 'none', dirty: true, // 新規行は未保存として扱う
    };
    setRows((rs) => [...rs, blank]);
    setOpenRowId(id);
  };

  // ── 派生値 ──
  // 上部タブに表示する件数（全件／未検収＝done=false／超過＝judge='over'）
  const counts = useMemo(() => ({
    all: rows.length,
    uninspected: rows.filter((r) => !r.done).length,
    over: rows.filter((r) => r.judge === 'over').length,
  }), [rows]);
  const dirtyCount = useMemo(() => rows.filter((r) => r.dirty).length, [rows]); // 未保存件数（一括保存ボタンの活性判定にも使用）

  // ソート用の値を列キーごとに取り出す。数値列は数値として比較できるよう変換し、
  // 未入力は末尾/先頭に寄るよう -Infinity や 0 をフォールバックにする。
  const getSortValue = (r: StatusRow, key: string): string | number => {
    switch (key) {
      case 'planNo': return r.planNo;
      case 'name': return r.name;
      case 'hq': return r.hq;
      case 'unit': return r.unit;
      case 'applyPeriod': return r.applyPeriod;
      case 'applyAmountM': return r.applyAmountM ?? -Infinity;
      case 'inspectDate': return r.inspectDate;
      case 'inspectAmountJpy': return Number((r.inspectAmountJpy || '').replace(/,/g, '')) || 0;
      case 'judge': return r.judge;
      case 'done': return r.done ? 1 : 0;
      default: return '';
    }
  };

  // 列フィルタ：対象フィールドの取得と一致判定
  const textMatch = (v: unknown, q: string) => !q || String(v ?? '').toLowerCase().includes(q.toLowerCase()); // 大文字小文字を無視した部分一致判定
  // 列フィルタの入力値と比較するため、行から列キーに対応する文字列を取り出す
  const filterField = (r: StatusRow, key: string): string => {
    switch (key) {
      case 'planNo': return r.planNo;
      case 'name': return r.name;
      case 'hq': return r.hq;
      case 'dept': return r.dept;
      case 'unit': return r.unit;
      case 'applyPeriod': return r.applyPeriod;
      case 'applyAmountM': return String(r.applyAmountM ?? '');
      case 'coCode': return r.coCode;
      case 'companies': return r.companies.join('、');
      case 'orderCompanies': return r.orderCompanies.join('、');
      case 'makers': return r.makers.map((m) => m.name).join('、');
      case 'orderAmount': return `${r.orderCurrency} ${r.orderAmount}`;
      case 'inspectDate': return r.inspectDate;
      case 'inspectAmountJpy': return r.inspectAmountJpy;
      default: return '';
    }
  };
  // 全ての列フィルタ（judge/done を含む）が一致する行だけを残す
  const matchRow = (r: StatusRow) => Object.entries(colFilters).every(([k, q]) => {
    if (!q) return true;
    if (k === 'judge') return r.judge === q; // 判定列は完全一致
    if (k === 'done') return (r.done ? 'done' : 'todo') === q; // 完了状態も完全一致
    return textMatch(filterField(r, k), q); // その他は部分一致
  });
  const hasActiveFilter = Object.values(colFilters).some(Boolean); // 何らかの列フィルタが設定中か（クリアボタンの表示制御などに使用）

  // 画面に表示する行：上部タブ（全件/未検収/超過）＋列フィルタで絞り込み、
  // 現在のソート設定（sort.key / sort.ascending）で並べ替えた結果
  const visibleRows = useMemo(() => {
    const base = rows.filter((r) =>
      (filter === 'uninspected' ? !r.done : filter === 'over' ? r.judge === 'over' : true) && matchRow(r),
    );
    return [...base].sort((a, b) => {
      const av = getSortValue(a, sort.key);
      const bv = getSortValue(b, sort.key);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'ja'); // 文字列は日本語ロケールで比較
      return sort.ascending ? cmp : -cmp;
    });
  }, [rows, filter, sort, colFilters]);

  const openRow = rows.find((r) => r.id === openRowId) ?? null; // 現在ドロワーで開いている行（なければnull）
  // 「超過」判定なのに超過理由が未入力の場合はエラー扱い（保存/入力欄の警告表示に使用）
  const overReasonError = !!openRow && openRow.judge === 'over' && !openRow.overReason?.trim();
  const expanded = !leftCollapsed; // 左側の折りたたみ状態の反転（表示用の可読変数）
  const cols = COLS.filter((c) => expanded || !c.ref); // 折りたたみ時は参考列（ref）を非表示にする
  const minW = cols.reduce((a, c) => a + c.w, 0); // テーブル全体の最小幅（各列幅の合計）
  const aCount = cols.filter((c) => c.grp === 'A').length; // グループA（基本情報）の列数
  const planNoW = cols[0].w; // 「設備件名」列の左固定オフセット計算用（先頭列＝設備計画No.の幅）
  const bcCount = cols.filter((c) => c.grp === 'B' || c.grp === 'C').length; // グループB・C（検収関連）の列数

  // input/textarea/select 共通の onChange ハンドラを生成するファクトリ。
  // ドロワー内の各入力欄から呼び出し、対象行(id)・対象フィールド(key)の値を更新する。
  const onInput = (id: string, key: keyof StatusRow) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setField(id, key, e.target.value as never);

  // 列見出しのソートアイコンボタン。現在のソート列なら昇順/降順アイコンを表示し、
  // クリックで同じ列なら昇順⇔降順をトグル、別の列なら昇順で新規ソートにする。
  const renderSortButton = (col: string) => (
    <IconButton
      aria-label="sort" h="16px" minW="16px" size="xs" variant="ghost" ml={1} verticalAlign="middle"
      icon={sort.key === col ? (sort.ascending ? <FaSortAmountUp /> : <FaSortAmountDown />) : <FaSort color="#A0AEC0" />}
      onClick={() => setSort((s) => (s.key === col ? { key: col, ascending: !s.ascending } : { key: col, ascending: true }))}
    />
  );

  // データ行の <Td> に共通で使うスタイル（列区切り線・文字サイズ・縦パディング）
  const tdProps = { borderRight: '1px solid', borderColor: 'gray.200', fontSize: 'sm', py: 2 } as const;

  type HeadOpts = {
    colKey: string; grp: ColDef['grp']; title: ReactNode; sortKey?: string;
    align?: 'left' | 'center' | 'right'; filter?: 'text' | 'select' | 'none';
    options?: { value: string; label: string }[]; brw?: string;
    stickyLeft?: number; stickyRight?: number; // 列固定（左/右）のオフセット(px)。指定時に position: sticky を付与
  };
  // 見出しセル：タイトル＋ソート＋列フィルタ（現行検索画面と同じ構成）。
  // ※コンポーネントではなく関数で返すため、入力中もフォーカスを失わない。
  const head = ({ colKey, grp, title, sortKey, align = 'left', filter = 'text', options, brw, stickyLeft, stickyRight }: HeadOpts) => {
    const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
    const sticky = stickyLeft !== undefined
      ? { position: 'sticky' as const, left: stickyLeft, zIndex: 3 }
      : stickyRight !== undefined
        ? { position: 'sticky' as const, right: stickyRight, zIndex: 3 }
        : {};
    return (
      <Th borderRight="1px solid" borderRightWidth={brw || '1px'} borderColor="gray.200"
        bg={GRP_TH_BG[grp]} color={GRP_TH_FG[grp]} fontSize="xs" py={2} verticalAlign="top" {...sticky}>
        <Flex align="center" justify={justify}>
          <Box as="span" whiteSpace="nowrap">{title}</Box>
          {sortKey && renderSortButton(sortKey)}
        </Flex>
        {/* テキスト絞り込み欄（部分一致） */}
        {filter === 'text' && (
          <Input size="xs" mt={1} bg="white" fontWeight="normal" placeholder="絞込"
            value={colFilters[colKey] || ''} onChange={(e) => setColFilter(colKey, e.target.value)} />
        )}
        {/* 選択式の絞り込み欄（judge/done など値が限定される列用、完全一致） */}
        {filter === 'select' && (
          <Select size="xs" mt={1} bg="white" fontWeight="normal"
            value={colFilters[colKey] || ''} onChange={(e) => setColFilter(colKey, e.target.value)}>
            <option value="">すべて</option>
            {options?.map((op) => <option key={op.value} value={op.value}>{op.label}</option>)}
          </Select>
        )}
      </Th>
    );
  };

  return (
    <Box bg="gray.50" minH="90vh" display="flex" flexDirection="column">

      {/* ツールバー：フィルタ / 未保存 / 操作 */}
      <Flex px={4} py={2} align="center" justify="space-between" gap={3} wrap="wrap">
        <HStack spacing={2}>
          {([
            ['all', `全件 ${counts.all}`],
            ['uninspected', `未検収 ${counts.uninspected}`],
            ['over', `超過 ${counts.over}`],
          ] as [StatusFilter, string][]).map(([key, label]) => (
            <Button key={key} size="sm" variant={filter === key ? 'solid' : 'outline'}
              colorScheme={filter === key ? 'blue' : 'gray'} onClick={() => setFilter(key)}>
              {label}
            </Button>
          ))}
        </HStack>
        <HStack spacing={3}>
          <Text fontSize="xs" fontWeight="bold" color={dirtyCount ? 'orange.600' : 'gray.500'}>
            {dirtyCount ? `● ${dirtyCount} 件 未保存` : '✓ 保存済み'}
          </Text>
          {/* <Button size="sm" colorScheme={dirtyCount ? 'orange' : 'gray'} isDisabled={!dirtyCount} onClick={saveAll}>
            💾 一括保存
          </Button> */}
          <Button size="sm" variant="outline" colorScheme="blue" borderStyle="dashed" onClick={addNewRow}>
            ＋ 新規行
          </Button>
          <ButtonGroup size="sm" isAttached>
            <Button colorScheme={inputMode === 'manual' ? 'green' : 'gray'} isLoading={isSpreadLoading}
              onClick={handleManualClick}>
              画面上で入力
            </Button>
            <Button colorScheme={inputMode === 'google' ? 'green' : 'gray'} isLoading={isSpreadLoading}
              onClick={handleGoogleSpreadClick}
              title="行データをスプレッドシートに書き出し、まとめて入力する">
              GoogleSpread
            </Button>
          </ButtonGroup>
          <Button size="sm" colorScheme="blue" onClick={handleSyncData} disabled={inputMode === 'manual'} isLoading={isSpreadLoading}
            title="GoogleSpreadの最新値を一覧へ反映する">
            データ連携
          </Button>
        </HStack>
      </Flex>

      {/* 一覧カード（現行検索画面と同じ器：白カード＋件数バー＋スクロール領域） */}
      <Box flex={1} minH={0} mx={4} mb={4} bg="white" borderRadius="lg" boxShadow="sm"
        border="1px solid" borderColor="gray.200" overflow="hidden" display="flex" flexDirection="column">
        <Flex px={4} py={2} borderBottom="1px solid" borderColor="gray.100" flexShrink={0} align="center" justify="space-between" gap={3} wrap="wrap">
          <HStack spacing={3}>
            <Text fontSize="sm" color="gray.600">
              件数: <Text as="span" fontWeight="bold" color="gray.800">{visibleRows.length}</Text> 件
            </Text>
            <Button size="xs" variant="outline" colorScheme="yellow" onClick={() => setLeftCollapsed((v) => !v)}>
              {leftCollapsed ? '▸ 経営管理部 参照を表示' : '▾ 経営管理部 参照を隠す'}
            </Button>
          </HStack>
          {hasActiveFilter && (
            <Button size="xs" variant="ghost" colorScheme="red" onClick={clearColFilters}>✕ 絞込をクリア</Button>
          )}
        </Flex>
        <Box flex={1} minH={0} overflow="auto">
          <Table variant="simple" size="sm" minW={`${minW}px`} sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              {cols.map((c) => <col key={c.k} style={{ width: `${c.w}px` }} />)}
            </colgroup>
            <Thead position="sticky" top={0} zIndex={1}>
              {/* グループ見出し行 */}
              <Tr>
                {/* 「設備計画No./設備件名」列は左固定にするため、見出しラベルを2列分だけ切り出す */}
                <Th colSpan={2} borderRight="2px solid" borderColor="gray.200" bg="yellow.100" color="yellow.800" fontSize="xs" textAlign="left" whiteSpace="nowrap"
                  position="sticky" left={0} zIndex={4}>
                  経営管理部 参照（CEAS・編集不可）
                </Th>
                {aCount > 2 && (
                  <Th colSpan={aCount - 2} borderRight="2px solid" borderColor="gray.200" bg="yellow.100" color="yellow.800" fontSize="xs" textAlign="left" whiteSpace="nowrap" />
                )}
                <Th colSpan={bcCount} borderRight="1px solid" borderColor="gray.200" bg="green.100" color="green.800" fontSize="xs" textAlign="center" whiteSpace="nowrap">
                  各事業部 状況報告（要約）— 編集は「編集」から ▶
                </Th>
                {/* 「操作」列は右固定 */}
                <Th bg="gray.100" color="gray.600" fontSize="xs" textAlign="center" position="sticky" right={0} zIndex={4}>操作</Th>
              </Tr>
              {/* 列見出し行（ソート＋列フィルタ） */}
              <Tr>
                {head({ colKey: 'planNo', grp: 'A', title: '設備計画No.', sortKey: 'planNo', stickyLeft: 0 })}
                {head({ colKey: 'name', grp: 'A', title: '設備件名', sortKey: 'name', brw: expanded ? '1px' : '2px', stickyLeft: planNoW })}
                {expanded && (
                  <>
                    {head({ colKey: 'hq', grp: 'A', title: '製造本部名', sortKey: 'hq' })}
                    {head({ colKey: 'dept', grp: 'A', title: '部門名等' })}
                    {head({ colKey: 'unit', grp: 'A', title: '事業部名等', sortKey: 'unit' })}
                    {head({ colKey: 'applyPeriod', grp: 'A', title: '設備申請時期', sortKey: 'applyPeriod', align: 'center' })}
                    {head({ colKey: 'applyAmountM', grp: 'A', title: '申請額(百万)', sortKey: 'applyAmountM', align: 'right' })}
                    {head({ colKey: 'coCode', grp: 'A', title: '会社コード', align: 'center' })}
                    {head({ colKey: 'companies', grp: 'A', title: '会社名', brw: '2px' })}
                  </>
                )}
                {head({ colKey: 'orderCompanies', grp: 'B', title: '発注先会社名' })}
                {head({ colKey: 'makers', grp: 'B', title: '設備メーカー名' })}
                {head({ colKey: 'orderAmount', grp: 'B', title: '発注金額', align: 'right' })}
                {head({ colKey: 'inspectDate', grp: 'B', title: '検収時期', sortKey: 'inspectDate', align: 'center' })}
                {head({
                  colKey: 'inspectAmountJpy', grp: 'C', align: 'right', sortKey: 'inspectAmountJpy',
                  title: <>検収額(円換算)<Text as="span" fontSize="9px" color="green.500"> 自動</Text></>
                })}
                {head({
                  colKey: 'judge', grp: 'C', align: 'center', sortKey: 'judge', filter: 'select',
                  title: <>申請額判定<Text as="span" fontSize="9px" color="green.500"> 自動</Text></>,
                  options: [{ value: 'ok', label: '申請額内' }, { value: 'over', label: '超過' }, { value: 'none', label: '未入力' }]
                })}
                {head({ colKey: 'remarks', grp: 'B', title: '備考', align: 'center', filter: 'none' })}
                {head({
                  colKey: 'done', grp: 'B', title: '検収状態', sortKey: 'done', align: 'center', filter: 'select',
                  options: [{ value: 'done', label: '完了' }, { value: 'todo', label: '未完了' }]
                })}
                {head({ colKey: 'ops', grp: 'X', title: '操作', align: 'center', filter: 'none', stickyRight: 0 })}
              </Tr>
            </Thead>
            <Tbody>
              {visibleRows.map((r) => {
                const j = JUDGE_META[r.judge]; // 判定（申請額内／超過／未入力）に応じた色・ラベルを取得
                return (
                  // 未保存（dirty）行は背景をオレンジにして目立たせる
                  <Tr key={r.id} bg={r.dirty ? 'orange.50' : 'white'} _hover={{ bg: r.dirty ? 'orange.100' : 'gray.50' }}>
                    <Td {...tdProps} position="sticky" left={0} zIndex={1} bg={r.dirty ? 'orange.50' : 'white'}>
                      <HStack spacing={2}>
                        {/* 未保存であることを示すドット（保存済みなら透明で非表示） */}
                        <Box w="7px" h="7px" borderRadius="full" flexShrink={0} bg={r.dirty ? 'orange.400' : 'transparent'} />
                        <TruncatedCell label={r.planNo}><Text fontWeight="semibold">{r.planNo}</Text></TruncatedCell>
                      </HStack>
                    </Td>
                    {/* 設備件名リンク：クリックで編集ドロワーを開く */}
                    <Td {...tdProps} borderRightWidth={expanded ? '1px' : '2px'} position="sticky" left={`${planNoW}px`} zIndex={1} bg={r.dirty ? 'orange.50' : 'white'}>
                      <TruncatedCell label={r.name}>
                        <Link color="blue.500" textDecoration="underline" cursor="pointer" onClick={() => setOpenRowId(r.id)}>
                          {r.name}
                        </Link>
                      </TruncatedCell>
                    </Td>
                    {/* 左側「経営管理部 参照」列群：leftCollapsed が true の間は非表示 */}
                    {expanded && (
                      <>
                        <Td {...tdProps} color="gray.600"><TruncatedCell label={r.hq}>{r.hq}</TruncatedCell></Td>
                        <Td {...tdProps} color="gray.600"><TruncatedCell label={r.dept}>{r.dept}</TruncatedCell></Td>
                        <Td {...tdProps}><TruncatedCell label={r.unit}>{r.unit}</TruncatedCell></Td>
                        <Td {...tdProps} color="gray.600" textAlign="center"><TruncatedCell label={r.applyPeriod}>{r.applyPeriod}</TruncatedCell></Td>
                        <Td {...tdProps} fontWeight="semibold" textAlign="right"><TruncatedCell label={String(r.applyAmountM ?? '-')}>{r.applyAmountM ?? '-'}</TruncatedCell></Td>
                        <Td {...tdProps} color="gray.600" textAlign="center"><TruncatedCell label={r.coCode}>{r.coCode}</TruncatedCell></Td>
                        <Td {...tdProps} borderRightWidth="2px">
                          <TruncatedCell label={r.companies.filter((c) => c && c !== '-').join('、') || '-'}>
                            {r.companies.filter((c) => c && c !== '-').join('、') || '-'}
                          </TruncatedCell>
                        </Td>
                      </>
                    )}
                    <Td {...tdProps}><SummaryChip items={r.orderCompanies} /></Td>
                    <Td {...tdProps}><SummaryChip items={r.makers.map((m) => m.name)} /></Td>
                    <Td {...tdProps} textAlign="right">
                      <TruncatedCell label={r.orderAmount ? `${r.orderCurrency} ${r.orderAmount}` : ''}>
                        {r.orderAmount ? `${r.orderCurrency} ${r.orderAmount}` : '—'}
                      </TruncatedCell>
                    </Td>
                    <Td {...tdProps} textAlign="center"><TruncatedCell label={r.inspectDate ? r.inspectDate.replace(/-/g, '/') : ''}>{r.inspectDate ? r.inspectDate.replace(/-/g, '/') : '—'}</TruncatedCell></Td>
                    {/* 検収額(円換算)：自動算出項目のため緑背景で区別 */}
                    <Td {...tdProps} bg="green.50" color="green.700" fontWeight="semibold" textAlign="right">
                      <TruncatedCell label={r.inspectAmountJpy}>{r.inspectAmountJpy || '-'}</TruncatedCell>
                    </Td>
                    {/* 申請額判定バッジ：申請額と検収額(円換算)を比較した自動判定結果。ホバーで内訳を表示 */}
                    <Td {...tdProps} bg="green.50" textAlign="center">
                      <Tooltip hasArrow label={`申請額 ${r.applyAmountM ?? '—'}百万円 ／ 検収額(円換算) ${r.inspectAmountJpy || '—'}千円`}>
                        <Badge colorScheme={j.scheme} borderRadius="full" px={2}>{j.text}</Badge>
                      </Tooltip>
                    </Td>
                    {/* 備考アイコン：備考が入力されている行のみ不透明度を上げて目立たせる */}
                    <Td {...tdProps} textAlign="center">
                      <Tooltip hasArrow label={r.remarks || '（備考なし）'}>
                        <Text as="span" fontSize="md" opacity={r.remarks ? 1 : 0.28} cursor="help">📝</Text>
                      </Tooltip>
                    </Td>
                    <Td {...tdProps} textAlign="center">
                      <Badge colorScheme={r.done ? 'green' : 'gray'} borderRadius="full" px={2}>{r.done ? '完了' : '未完了'}</Badge>
                    </Td>
                    <Td {...tdProps} textAlign="center" position="sticky" right={0} zIndex={1} bg={r.dirty ? 'orange.50' : 'white'}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => setOpenRowId(r.id)}>編集</Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
      <Text px={4} pb={3} fontSize="xs" color="gray.500">
        ※ 参照列（黄）は「経営管理部 参照」トグルで展開。発注先/メーカーは「社名 ＋N」で省略、長い値はマウスオーバーで全文表示。
      </Text>

      {/* 編集ドロワー */}
      <Drawer isOpen={!!openRow} onClose={() => setOpenRowId(null)} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          {openRow && (
            <>
              <DrawerHeader borderBottomWidth="1px">
                {/* applyAmountM が未設定＝経営管理部データがまだ紐付いていない「新規行」とみなす */}
                <Text fontSize="xs" color="gray.500" fontWeight="bold">
                  状況報告 編集{openRow.applyAmountM == null ? '（新規行）' : ''}
                </Text>
                <Heading size="sm" mt={1}>{openRow.planNo}　{openRow.name}</Heading>
                <DrawerCloseButton />
              </DrawerHeader>

              <DrawerBody>
                {/* A: 申請データ（参照） */}
                <Text fontSize="xs" fontWeight="bold" color="yellow.700" mb={2}>📌 申請データ（参照）</Text>
                <Box bg="yellow.50" borderWidth="1px" borderColor="yellow.200" borderRadius="md" p={3} fontSize="sm" color="yellow.900" mb={4}>
                  <Text>製造本部：{openRow.hq}</Text>
                  <Text>部門／事業部：{openRow.dept}｜{openRow.unit}</Text>
                  <Text>申請時期：{openRow.applyPeriod}　／　申請金額：<b>{openRow.applyAmountM ?? '—'} 百万円</b></Text>
                  <Text>会社：{openRow.coCode}　{openRow.companies.join('、')}</Text>
                </Box>

                {/* B: 発注 */}
                <Text fontSize="xs" fontWeight="bold" color="green.700" mb={2}>📝 発注</Text>
                <SimpleGrid columns={2} spacing={3} mb={4}>
                  <Field label="設備申請NO.">
                    <Input size="sm" value={openRow.applyNo} onChange={onInput(openRow.id, 'applyNo')} />
                  </Field>
                  <Field label="発注時期（実施）">
                    <Input size="sm" type="date" value={openRow.orderDate} onChange={onInput(openRow.id, 'orderDate')} />
                  </Field>
                </SimpleGrid>

                <Field label="発注先会社名（複数可）" mb={4}>
                  <VStack align="stretch" spacing={2}>
                    {openRow.orderCompanies.map((oc, i) => (
                      <HStack key={i}>
                        <Input size="sm" value={oc} placeholder="社名を入力"
                          onChange={(e) => setOrderCompany(openRow.id, i, e.target.value)} />
                        <IconButton aria-label="削除" size="sm" variant="ghost" colorScheme="red"
                          icon={<span>✕</span>} onClick={() => removeOrderCompany(openRow.id, i)} />
                      </HStack>
                    ))}
                    <Button size="xs" variant="outline" colorScheme="blue" borderStyle="dashed" alignSelf="flex-start"
                      onClick={() => addOrderCompany(openRow.id)}>＋ 発注先を追加</Button>
                  </VStack>
                </Field>

                {/* B: メーカー内訳（1対多）：メーカーごとに通貨・金額を持ち、検収額(円換算)の元データとなる */}
                <Text fontSize="xs" fontWeight="bold" color="green.700" mb={2}>🏭 設備メーカー（通貨・金額の内訳）</Text>
                <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={4}>
                  <SimpleGrid columns={4} spacing={2} px={3} py={2} bg="gray.50" fontSize="xs" fontWeight="bold" color="gray.600"
                    templateColumns="1.8fr 0.8fr 1fr 34px">
                    <Text>メーカー名</Text><Text>通貨</Text><Text>金額(千)</Text><Box />
                  </SimpleGrid>
                  {openRow.makers.map((m, i) => (
                    <SimpleGrid key={i} columns={4} spacing={2} px={3} py={2} borderTopWidth="1px" alignItems="center"
                      templateColumns="1.8fr 0.8fr 1fr 34px">
                      <Input size="xs" value={m.name} onChange={(e) => setMakerField(openRow.id, i, 'name', e.target.value)} />
                      <Select size="xs" value={m.currency}
                        onChange={(e) => setMakerField(openRow.id, i, 'currency', e.target.value as CurrencyCode)}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </Select>
                      <Input size="xs" textAlign="right" value={m.amount} onChange={(e) => setMakerField(openRow.id, i, 'amount', e.target.value)} />
                      <IconButton aria-label="削除" size="xs" variant="ghost" colorScheme="red" icon={<span>🗑</span>}
                        onClick={() => removeMaker(openRow.id, i)} />
                    </SimpleGrid>
                  ))}
                  <Box px={3} py={2} borderTopWidth="1px">
                    <Button size="xs" variant="outline" colorScheme="blue" borderStyle="dashed"
                      onClick={() => addMaker(openRow.id)}>＋ メーカーを追加</Button>
                  </Box>
                </Box>

                <SimpleGrid columns={2} spacing={3} mb={4}>
                  <Field label="発注元コード">
                    <Input size="sm" value={openRow.orderSrcCode} onChange={onInput(openRow.id, 'orderSrcCode')} />
                  </Field>
                  <Field label="発注元会社名">
                    <Input size="sm" value={openRow.orderSrcCompany} onChange={onInput(openRow.id, 'orderSrcCompany')} />
                  </Field>
                </SimpleGrid>

                {/* B: 検収・支払 */}
                <Text fontSize="xs" fontWeight="bold" color="green.700" mb={2}>✅ 検収・支払</Text>
                <SimpleGrid columns={2} spacing={3} mb={3}>
                  <Field label="検収時期（実施）">
                    <Input size="sm" type="date" value={openRow.inspectDate} onChange={onInput(openRow.id, 'inspectDate')} />
                  </Field>
                  <Field label="支払予定時期（任意）">
                    <Input size="sm" type="month" value={openRow.payPeriod} onChange={onInput(openRow.id, 'payPeriod')} />
                  </Field>
                  <Field label="支払元コード">
                    <Input size="sm" value={openRow.paySrcCode} onChange={onInput(openRow.id, 'paySrcCode')} />
                  </Field>
                  <Field label="支払元会社">
                    <Input size="sm" value={openRow.paySrcCompany} onChange={onInput(openRow.id, 'paySrcCompany')} />
                  </Field>
                </SimpleGrid>

                {/* C: 円換算（自動） */}
                <Flex bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={3} mb={3}
                  justify="space-between" align="center" color="green.700">
                  <Text fontSize="sm">検収金額（円換算・自動）</Text>
                  <Text fontWeight="bold">{openRow.inspectAmountJpy || '—'} 千円</Text>
                </Flex>

                {/* 超過警告（overReason / additionalApplied は暫定項目・要データ設計。TODO: 永続化方式未確定） */}
                {openRow.judge === 'over' && (
                  <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3} mb={3} color="red.700">
                    <Text fontWeight="bold" mb={2}>⚠️ 検収金額（円換算）が申請金額を超過しています</Text>
                    <Checkbox mb={2} isChecked={!!openRow.additionalApplied}
                      onChange={(e) => setField(openRow.id, 'additionalApplied', e.target.checked)}>
                      追加申請済み確認
                    </Checkbox>
                    <Textarea size="sm" placeholder="超過の理由（超過時は入力必須）"
                      value={openRow.overReason ?? ''}
                      isInvalid={overReasonError}
                      onChange={(e) => setField(openRow.id, 'overReason', e.target.value)} />
                    {overReasonError && (
                      <Text fontSize="xs" color="red.600" mt={1}>超過理由の入力は必須です。</Text>
                    )}
                  </Box>
                )}

                <Field label="備考（上限1,000文字）" mb={4}>
                  <Textarea size="sm" value={openRow.remarks} maxLength={1000} placeholder="備考を入力"
                    onChange={(e) => setField(openRow.id, 'remarks', e.target.value)} />
                </Field>

                <Checkbox isChecked={openRow.done} colorScheme="green" onChange={() => toggleDone(openRow.id)}>
                  検収完了
                </Checkbox>
              </DrawerBody>

              <DrawerFooter borderTopWidth="1px" justifyContent="space-between">
                {/* 削除は即実行せず確認ダイアログ（AlertDialog）を挟む */}
                <Button variant="outline" colorScheme="red" onClick={() => setConfirmRowDelete(true)}>この行を削除</Button>
                <HStack>
                  <Button variant="outline" onClick={() => setOpenRowId(null)}>閉じる</Button>
                  {/* 超過理由未入力の間は保存を無効化し、理由をツールチップで案内 */}
                  <Tooltip isDisabled={!overReasonError} label="超過理由を入力してください" hasArrow>
                    <Button colorScheme="blue" onClick={saveDrawer} isDisabled={overReasonError}>保存</Button>
                  </Tooltip>
                </HStack>
              </DrawerFooter>
            </>
          )}
        </DrawerContent>
      </Drawer>

      {/* 行削除確認ダイアログ：ドロワー右下の「この行を削除」から呼び出される */}
      <ConfirmDialog
        isOpen={confirmRowDelete}
        onClose={() => setConfirmRowDelete(false)}
        onConfirm={() => { if (openRow) removeRow(openRow.id); }}
        title="削除確認"
        message={`「${openRow?.planNo ?? ''} ${openRow?.name ?? ''}」を削除します。よろしいでしょうか？`}
        warning="※入力中のデータは削除され、再表示できません。"
      />
    </Box>
  );
}

/** ラベル＋入力の縦積みフィールド */
function Field({ label, children, mb }: { label: string; children: ReactNode; mb?: number }) {
  return (
    <VStack align="stretch" spacing={1} mb={mb}>
      <Text fontSize="xs" fontWeight="bold" color="gray.600">{label}</Text>
      {children}
    </VStack>
  );
}
